import { escapeHtml } from "../utils/salesLedger";
import { compareInventoryLabels, compareInventoryRows, formatInventoryNumber, isInventorySoldOut, matchesInventorySearch } from "../utils/inventoryDisplay";
import { printWithTheme } from "../utils/typography";
import { englishError } from "../utils/databaseErrors";
// src/components/StockAllShowroomsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { availableQuantity, PRODUCT_TYPES, reservedQuantity } from "../utils/stock";

/* ===== Helpers للعرض ===== */
const treatMap = { epoxy: "Epoxy", resin: "Resin", none: "None" };

function formatVariant(d) {
  const face = d?.face_treatment || "none";
  const back = d?.back_treatment || "none";
  const pol = !!d?.is_polished;
  return `Front: ${treatMap[face]} | Back: ${treatMap[back]} | ${pol ? "Polished" : "Unpolished"}`;
}

function codeWithSuffix(blockCode, dist) {
  const sfx = (dist?.variant_suffix || "").trim();
  if (sfx) return `${blockCode}-${sfx}`;

  const f = dist?.face_treatment || "none";
  const b = dist?.back_treatment || "none";
  const p = dist?.is_polished ? "P" : "";
  const short =
    (f === "epoxy" ? "FE" : f === "resin" ? "FR" : "") +
    (b === "epoxy" ? "BE" : b === "resin" ? "BR" : "") +
    p;
  return short ? `${blockCode}-${short}` : blockCode;
}


export default function StockAllShowroomsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selMaterial, setSelMaterial] = useState("ALL");
  const [selSupplier, setSelSupplier] = useState("ALL");
  const [selShowroom, setSelShowroom] = useState("ALL");
  const [selFace, setSelFace] = useState("ALL");
  const [selBack, setSelBack] = useState("ALL");
  const [selPolish, setSelPolish] = useState("ALL");

  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError("");

    const { data, error } = await supabase
      .from("block_distribution")
      .select(
        `
        id, slabs_count, variant_suffix, face_treatment, back_treatment, is_polished, showroom_id,
        block:block!fk_bd_block (
          id, code, product_type, material_id, customer_id, total_area, slabs_number, notes_text,
          material:material_id ( id, name ),
          customer:customer_id ( id, name )
        ),
        showroom:showroom!fk_bd_showroom ( id, name )
      `
      );

    if (error) {
      setError(englishError(error.message) || "Unable to load");
      setLoading(false);
      return;
    }

    const { data: bookings, error: bookingError } = await supabase.from("reservation").select("*");
    if (bookingError) { setError("Unable to load reservations: " + englishError(bookingError.message)); setLoading(false); return; }
    const flat = (data || []).map((d) => {
      const block = d.block || {};
      const material = block.material || {};
      const customer = block.customer || {};
      const showroom = d.showroom || {};

      const area =
        block.total_area && block.slabs_number
          ? (Number(block.total_area) * Number(d.slabs_count)) / Number(block.slabs_number)
          : "";

      return {
        showroomId: showroom.id,
        showroomName: showroom.name || (showroom.id ? `Showroom ${showroom.id}` : "-"),
        blockId: block.id,
        notes: block.notes_text,
        code: block.code,
        codeLabel: codeWithSuffix(block.code, d),
        productType: PRODUCT_TYPES[block.product_type || "slab"],
        materialId: block.material_id,
        materialName: material.name || block.material_id || "-",
        supplierName: customer.name || "-",
        slabs: Number(d.slabs_count) || 0,
        reserved: reservedQuantity(bookings || [], d.id),
        available: availableQuantity(d, bookings || []),
        area,
        face: d.face_treatment || "none",
        back: d.back_treatment || "none",
        isPolished: !!d.is_polished,
        dist: d,
      };
    });

    setRows(flat);
    setLoading(false);
  }

  const materialOpts = useMemo(
    () => ["ALL", ...Array.from(new Set(rows.map((r) => r.materialName).filter(Boolean))).sort(compareInventoryLabels)],
    [rows]
  );
  const supplierOpts = useMemo(
    () => ["ALL", ...Array.from(new Set(rows.map((r) => r.supplierName).filter(Boolean)))],
    [rows]
  );
  const showroomOpts = useMemo(
    () => ["ALL", ...Array.from(new Set(rows.map((r) => r.showroomName).filter(Boolean)))],
    [rows]
  );

  const visibleRows = useMemo(() => rows.filter(r => {
    if (selMaterial !== "ALL" && r.materialName !== selMaterial) return false;
    if (selSupplier !== "ALL" && r.supplierName !== selSupplier) return false;
    if (selShowroom !== "ALL" && r.showroomName !== selShowroom) return false;
    if (selFace !== "ALL" && r.face !== selFace) return false;
    if (selBack !== "ALL" && r.back !== selBack) return false;
    if (selPolish === "P" && !r.isPolished) return false;
    if (selPolish === "R" && r.isPolished) return false;
    return matchesInventorySearch(r, search);
  }).sort(compareInventoryRows), [rows, selMaterial, selSupplier, selShowroom, selFace, selBack, selPolish, search]);

  const byShowroom = useMemo(() => {
    const map = new Map();
    for (const r of visibleRows) {
      if (!map.has(r.showroomId))
        map.set(r.showroomId, { showroomName: r.showroomName, items: [] });
      map.get(r.showroomId).items.push(r);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ showroomId: id, ...v }));
  }, [visibleRows]);

  const resetFilters = () => {
    setSelMaterial("ALL");
    setSelSupplier("ALL");
    setSelShowroom("ALL");
    setSelFace("ALL");
    setSelBack("ALL");
    setSelPolish("ALL");
    setSearch("");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "PRINT", "width=1000,height=800");
    if (!printWindow) return;

    const logoUrl = "/logo.png";

    printWindow.document.write(`<!DOCTYPE html>
      <html lang="ar" dir="ltr">
      <head>
        <meta charset="UTF-8">
        <title>Print inventory</title>
        <style>
          body { font-family: Jost, 'Trebuchet MS', Arial, sans-serif; direction: ltr; text-align:left; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #d2cbc1; padding: 6px 8px; font-size: 12px; }
          th { background: #f2ede4; }
        </style>
      </head>
      <body>
        <div style="display:flex; align-items:center; justify-content:space-between;">
          <h2>Inventory report</h2>
          <img src="${logoUrl}" alt="Logo" style="height:60px;"/>
        </div>
        ${byShowroom
          .map(({ showroomName, items }) => {
            const totalSlabs = items.reduce((s, r) => s + (Number(r.slabs) || 0), 0);
            const totalArea = items.reduce((s, r) => s + (Number(r.area) || 0), 0);

            return `
            <h3>${showroomName}</h3>
            <table>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Product code</th>
                  <th>Material</th>
                  <th>Product type</th>
                  <th>Supplier</th>
                  <th>Quantity</th>
                  <th>Reserved</th>
                  <th>Available to sell</th>
                  <th>Area (m²)</th>
                  <th>Treatment</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${items
                  .map(
                    (r, idx) => `
                  <tr>
                    <td>${idx + 1}</td>
                    <td>${r.codeLabel}</td>
                    <td>${r.materialName}</td>
                    <td>${r.productType}</td>
                    <td>${r.supplierName}</td>
                    <td>${formatInventoryNumber(r.slabs)}</td>
                    <td>${formatInventoryNumber(r.reserved)}</td>
                    <td>${formatInventoryNumber(r.available)}</td>
                    <td>${isInventorySoldOut(r) ? "Sold out" : `${formatInventoryNumber(r.area)} m²`}</td>
                    <td>${formatVariant(r.dist)}</td>
                    <td style="white-space:pre-wrap;overflow-wrap:anywhere">${escapeHtml(r.notes || "-")}</td>
                  </tr>`
                  )
                  .join("")}
                <tr style="font-weight:bold;">
                  <td colspan="5">Total in ${showroomName}</td>
                  <td>${formatInventoryNumber(totalSlabs)}</td>
                  <td>${formatInventoryNumber(items.reduce((sum, row) => sum + row.reserved, 0))}</td>
                  <td>${formatInventoryNumber(items.reduce((sum, row) => sum + row.available, 0))}</td>
                  <td>${formatInventoryNumber(totalArea)} m²</td>
                  <td></td><td></td>
                </tr>
              </tbody>
            </table>`;
          })
          .join("")}
      </body>
      </html>`);

    printWindow.document.close();
    printWindow.focus();
    printWithTheme(printWindow);
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    background: "#faf8f4",
    borderRadius: 10,
    direction: "ltr",
    textAlign: "left",
    fontSize: 12,
  };
  const thtd = {
    borderBottom: "1px solid #d2cbc1",
    padding: "6px 8px",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  };

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 1100, margin: "auto", padding: 18 }}>
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 10 }}>Inventory across all showrooms</h2>

      {/* فلاتر بالدروب داون */}
      <div className="ui-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <label className="ui-field">Material<select value={selMaterial} onChange={(e) => setSelMaterial(e.target.value)}>
          <option value="ALL">All materials</option>
          {materialOpts.map((m) => (m === "ALL" ? null : <option key={m} value={m}>{m}</option>))}
        </select></label>

        <label className="ui-field">Search material or block code
          <input type="search" value={search} onChange={event => setSearch(event.target.value)} />
        </label>

        <label className="ui-field">Supplier<select value={selSupplier} onChange={(e) => setSelSupplier(e.target.value)}>
          <option value="ALL">All suppliers</option>
          {supplierOpts.map((s) => (s === "ALL" ? null : <option key={s} value={s}>{s}</option>))}
        </select></label>

        <label className="ui-field">Showroom<select value={selShowroom} onChange={(e) => setSelShowroom(e.target.value)}>
          <option value="ALL">All showrooms</option>
          {showroomOpts.map((s) => (s === "ALL" ? null : <option key={s} value={s}>{s}</option>))}
        </select></label>

        <label className="ui-field">Front treatment<select value={selFace} onChange={(e) => setSelFace(e.target.value)}>
          <option value="ALL">Front treatment</option>
          <option value="none">None</option>
          <option value="epoxy">Epoxy</option>
          <option value="resin">Resin</option>
        </select></label>

        <label className="ui-field">Back treatment<select value={selBack} onChange={(e) => setSelBack(e.target.value)}>
          <option value="ALL">Back treatment</option>
          <option value="none">None</option>
          <option value="epoxy">Epoxy</option>
          <option value="resin">Resin</option>
        </select></label>

        <label className="ui-field">Polish<select value={selPolish} onChange={(e) => setSelPolish(e.target.value)}>
          <option value="ALL">Polish</option>
          <option value="P">Polished</option>
          <option value="R">Unpolished</option>
        </select></label>

        <button className="ui-secondary" onClick={resetFilters}>Reset</button>
        <button className="ui-secondary" onClick={handlePrint}>Print</button>
        <button className="ui-secondary" onClick={fetchAll} disabled={loading}>Refresh inventory</button>
      </div>

      {error && (
        <div style={{ marginBottom: 8, color: "crimson", fontWeight: "bold" }}>
          {error}
        </div>
      )}

      {loading && <div>Loading...</div>}
      {!loading && !error && !visibleRows.length && <p>No records match the current filters.</p>}

      {!loading &&
        byShowroom.map(({ showroomId, showroomName, items }) => (
          <div className="ui-card"
            key={showroomId || "no-sh"}
            style={{
              margin: "22px 0",
              background: "#faf8f4",
              border: "1px solid #d2cbc1",
              borderRadius: 14,
              padding: 16,
            }}
          >
            <h3 style={{ color: "var(--accent, #514536)", margin: "0 0 10px 0" }}>{showroomName}</h3>

            <div className="ui-table-scroll"><table style={tableStyle}>
              <thead>
                <tr style={{ background: "#f2ede4" }}>
                  <th style={thtd}>No.</th>
                  <th style={thtd}>Product code</th>
                  <th style={thtd}>Material</th>
                  <th style={thtd}>Product type</th>
                  <th style={thtd}>Supplier</th>
                  <th style={thtd}>Quantity</th>
                  <th style={thtd}>Reserved</th>
                  <th style={thtd}>Available to sell</th>
                  <th style={thtd}>Area (m²)</th>
                  <th style={thtd}>Treatment</th>
                  <th style={thtd}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr key={`${showroomId}-${r.blockId}-${r.dist.id}`}>
                    <td style={thtd}>{idx + 1}</td>
                    <td style={thtd}>{r.codeLabel}</td>
                    <td style={thtd}>{r.materialName}</td>
                    <td style={thtd}>{r.productType}</td>
                    <td style={thtd}>{r.supplierName}</td>
                    <td style={thtd}>{formatInventoryNumber(r.slabs)}</td>
                    <td style={thtd}>{formatInventoryNumber(r.reserved)}</td>
                    <td style={thtd}>{formatInventoryNumber(r.available)}</td>
                    <td style={thtd}>{isInventorySoldOut(r) ? "Sold out" : `${formatInventoryNumber(r.area)} m²`}</td>
                    <td style={thtd}>{formatVariant(r.dist)}</td>
                    <td className="ui-table-notes" style={thtd}>{r.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        ))}
    </div>
  );
}
